import {
  BizNestClientError,
  EhttpController,
  FrontendLogger,
  IehttpControllerConfig
} from '@biz-nest/core-client';

/**
 * A client for handling manager-related operations within the application.
 * This class provides a wrapper around the HTTP client for making manager service requests.
 *
 * @class ManagerClient
 * @example
 * const config = { baseURL: 'https://api.example.com/manager' };
 * const managerClient = new ManagerClient(config);
 */
export class ManagerClient {
  /**
   * Static instance of the HTTP controller.
   * @private
   * @static
   * @type {EhttpController}
   */
  private static http: EhttpController;

  /**
   * Creates a new instance of ManagerClient.
   * Initializes the HTTP client with the provided configuration.
   *
   * @param {IehttpControllerConfig} config - Configuration object for the HTTP client.
   * @throws {BizNestClientError} If the provided configuration is invalid.
   *
   * @example
   * const managerClient = new ManagerClient({
   *   baseURL: 'https://api.example.com/manager',
   *   timeout: 5000
   * });
   */
  constructor(config: IehttpControllerConfig) {
    if (!config || typeof config !== 'object') {
      throw new BizNestClientError('config must be a valid configuration object');
    }

    FrontendLogger.debug('ManagerClient initialized');
    ManagerClient.http = new EhttpController(config);
  }

  /**
   * Gets the singleton instance of the HTTP client.
   * This provides access to the underlying HTTP methods for making requests.
   *
   * @static
   * @returns {EhttpController} The initialized HTTP client instance.
   * @throws {BizNestClientError} If the HTTP client has not been initialized.
   *
   * @example
   * // Using the HTTP client to make a GET request
   * const response = await ManagerClient.ehttp.get('/endpoint');
   */
  static get ehttp() {
    if (!ManagerClient.http) {
      throw new BizNestClientError('HTTP client not initialized. Call new ManagerClient() first.');
    }

    return ManagerClient.http;
  }
}
